import matter from 'gray-matter'
import { ProjectFrontmatter, BlogFrontmatter, MultilingualProject, MultilingualBlogPost } from '@/types/multilingual'

// 解析项目markdown
export function parseProjectMarkdown(markdownContent: string): {
  frontmatter: ProjectFrontmatter
  content: { zh: string; en: string }
} {
  const { data, content } = matter(markdownContent)
  
  // 分割中英文内容
  const sections = content.split(/^---\s*language:\s*(zh|en)\s*---$/gm)
  let zhContent = ''
  let enContent = ''
  
  for (let i = 1; i < sections.length; i += 2) {
    const language = sections[i].trim()
    const sectionContent = sections[i + 1]?.trim() || ''
    
    if (language === 'zh') {
      zhContent = sectionContent
    } else if (language === 'en') {
      enContent = sectionContent
    }
  }
  
  // 如果没有找到语言分割，使用整个内容作为中文
  if (!zhContent && !enContent) {
    zhContent = content.trim()
  }
  
  return {
    frontmatter: data as ProjectFrontmatter,
    content: { zh: zhContent, en: enContent }
  }
}

// 解析博客markdown
export function parseBlogMarkdown(markdownContent: string): {
  frontmatter: BlogFrontmatter
  content: { zh: string; en: string }
} {
  const { data, content } = matter(markdownContent)
  
  // 分割中英文内容
  const sections = content.split(/^---\s*language:\s*(zh|en)\s*---$/gm)
  let zhContent = ''
  let enContent = ''
  
  for (let i = 1; i < sections.length; i += 2) {
    const language = sections[i].trim()
    const sectionContent = sections[i + 1]?.trim() || ''
    
    if (language === 'zh') {
      zhContent = sectionContent
    } else if (language === 'en') {
      enContent = sectionContent
    }
  }
  
  // 如果没有找到语言分割，使用整个内容作为中文
  if (!zhContent && !enContent) {
    zhContent = content.trim()
  }
  
  return {
    frontmatter: data as BlogFrontmatter,
    content: { zh: zhContent, en: enContent }
  }
}

// 将解析的项目数据转换为数据库格式
export function convertToMultilingualProject(
  frontmatter: ProjectFrontmatter,
  content: { zh: string; en: string }
): Omit<MultilingualProject, 'id' | 'created_at' | 'updated_at'> {
  return {
    category_id: frontmatter.category,
    status: frontmatter.status,
    image: frontmatter.image,
    gallery: frontmatter.gallery || [],
    link: frontmatter.link || null,
    tags: {
      zh: frontmatter.tags?.zh || [],
      en: frontmatter.tags?.en || []
    },
    features: {
      zh: frontmatter.features?.zh || [],
      en: frontmatter.features?.en || []
    },
    tech_stack: frontmatter.tech_stack || [],
    metrics: frontmatter.metrics || {},
    start_date: frontmatter.start_date || null,
    end_date: frontmatter.end_date || null,
    sort_order: frontmatter.sort_order || 0,
    is_featured: frontmatter.is_featured || false,
    translations: {
      zh: {
        title: frontmatter.translations.zh.title,
        subtitle: frontmatter.translations.zh.subtitle || null,
        description: frontmatter.translations.zh.description,
        content: content.zh || null
      },
      en: {
        title: frontmatter.translations.en.title,
        subtitle: frontmatter.translations.en.subtitle || null,
        description: frontmatter.translations.en.description,
        content: content.en || null
      }
    }
  }
}

// 将解析的博客数据转换为数据库格式
export function convertToMultilingualBlog(
  frontmatter: BlogFrontmatter,
  content: { zh: string; en: string }
): Omit<MultilingualBlogPost, 'id' | 'created_at' | 'updated_at'> {
  return {
    slug: frontmatter.slug,
    author: frontmatter.author || null,
    cover_image: frontmatter.cover_image || null,
    tags: {
      zh: frontmatter.tags?.zh || [],
      en: frontmatter.tags?.en || []
    },
    published: frontmatter.published || false,
    published_at: frontmatter.published_at || null,
    translations: {
      zh: {
        title: frontmatter.translations.zh.title,
        description: frontmatter.translations.zh.description,
        content: content.zh || ''
      },
      en: {
        title: frontmatter.translations.en.title,
        description: frontmatter.translations.en.description,
        content: content.en || ''
      }
    }
  }
}

// 生成项目markdown模板
export function generateProjectMarkdownTemplate(): string {
  return `---
category: "launch100"
status: "completed"
image: "https://example.com/image.jpg"
gallery: []
link: "https://example.com"
tags:
  zh: ["标签1", "标签2"]
  en: ["tag1", "tag2"]
features:
  zh: ["功能1", "功能2"]
  en: ["feature1", "feature2"]
tech_stack: ["React", "Node.js", "MongoDB"]
metrics:
  users: 1000
  revenue: "$10k"
start_date: "2024-01-01"
end_date: "2024-06-01"
sort_order: 1
is_featured: true
translations:
  zh:
    title: "项目标题"
    subtitle: "项目副标题"
    description: "项目描述"
  en:
    title: "Project Title"
    subtitle: "Project Subtitle"
    description: "Project Description"
---

--- language: zh ---
这里是中文的项目详细内容。

可以包含多个段落和格式。

--- language: en ---
Here is the English project detailed content.

Can include multiple paragraphs and formatting.
`
}

// 生成博客markdown模板
export function generateBlogMarkdownTemplate(): string {
  return `---
slug: "my-blog-post"
author: "Author Name"
cover_image: "https://example.com/cover.jpg"
tags:
  zh: ["标签1", "标签2"]
  en: ["tag1", "tag2"]
published: true
published_at: "2024-01-01T00:00:00Z"
translations:
  zh:
    title: "博客标题"
    description: "博客描述"
  en:
    title: "Blog Title"
    description: "Blog Description"
---

--- language: zh ---
这里是中文的博客内容。

## 标题

内容...

--- language: en ---
Here is the English blog content.

## Title

Content...
`
}